// src/components/Policies/RefundPolicy.jsx
import React from 'react';
import styles from './Policies.module.css';
import { Link } from 'react-router-dom';
import { useTheme } from './context/ThemeContext';

const RefundPolicy = () => {
  const { theme } = useTheme();

  return (
    <div className={`${styles.policyContainer} ${theme === "dark" ? "dark" : ""} ${styles[theme]}`}>
      <div className={styles.policyHeader}>
        <h1 className={styles.title}>Refund, Return & Shipping Policy</h1>
        <p className={styles.effectiveDate}>Effective Date: December 2025</p>
      </div>
  
      <div className={styles.policyContent}>
        <div className={styles.intro}>
          <p>
            This policy outlines the procedures, timelines, and eligibility criteria for refunds, returns, and shipping
            related to products purchased from Pixel Moon Store.
          </p>
        </div>
  
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>1. Digital Products Refund Policy</h2>
          <div className={styles.subsection}>
            <h3>1.1 Final Sale & Non-Refundability</h3>
            <p>
              Digital items (esports credits, passes, in-game currency, virtual goods) are considered final sale once
              delivered. Due to their intangible and immediate use potential, they are non-refundable and non-returnable
              after successful delivery.
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>1.2 Delivery Confirmation</h3>
            <p>
              Digital goods delivered to the specified account, platform, or contact are considered accepted and
              complete.
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>1.3 Disclaimer on Licensing</h3>
            <p>The User purchases a non-transferable license to use the item, which does not constitute ownership.</p>
          </div>
          <div className={styles.subsection}>
            <h3>1.4 Incorrect Details</h3>
            <p>
              No refunds will be issued for orders successfully delivered using incorrect user-provided information
              (game ID, username, platform).
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>1.5 Failed Delivery Cases</h3>
            <p>
              If delivery fails due to a verified technical or processing error attributable to Pixel Moon Store, a
              replacement or refund may be provided after internal verification.
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>1.6 Order Cancellation</h3>
            <p>
              Orders for digital products cannot be cancelled once payment is completed, as delivery is instant or
              near-instant.
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>1.7 Processing Fees</h3>
            <p>Any payment gateway, processing, or convenience fees included in the transaction are non-refundable.</p>
          </div>
          <div className={styles.subsection}>
            <h3>1.8 Chargebacks</h3>
            <p>
              Initiating chargebacks for successfully delivered digital goods may result in account suspension and
              reporting to payment processors.
            </p>
          </div>
        </section>
  
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>2. Physical Products Return & Refund Policy</h2>
          <div className={styles.subsection}>
            <h3>2.1 Return Eligibility</h3>
            <p>
              Physical products are eligible for return or replacement only if received defective, damaged, or
              incorrect. Eligibility is subject to verification and proof (e.g., photos/videos).
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>2.2 Return Window</h3>
            <p>Return requests must be initiated within 7 days of delivery.</p>
          </div>
          <div className={styles.subsection}>
            <h3>2.3 Condition</h3>
            <p>
              Returned items must be unused, in original packaging, and include all accessories and documentation.
              Return shipping costs shall be borne by the User unless the return is due to an error attributable to
              Pixel Moon Store. Orders for physical products may be cancelled before dispatch. Once dispatched,
              cancellation is not permitted.
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>2.4 Refund Timeline (Physical)</h3>
            <p>
              Approved refunds will be processed within 7–10 business days after the returned item is received and
              physically inspected by GJ Mart. The credit will be applied to the original payment method.
            </p>
          </div>
        </section>
  
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>3. Shipping Policy</h2>
          <div className={styles.subsection}>
            <h3>3.1 Digital Delivery</h3>
            <p>Digital products are delivered electronically and do not involve physical shipping or tracking.</p>
          </div>
          <div className={styles.subsection}>
            <h3>3.2 Domestic Shipping (India)</h3>
            <p>
              Physical products are currently shipped within India. Tracking details will be shared once the order is
              dispatched.
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>3.3 Delivery Timelines Disclaimer</h3>
            <p>
              Estimated delivery times are indicative only and may vary due to logistics, force majeure events, or
              carrier delays.
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>3.4 Risk Transfer</h3>
            <p>
              The risk of loss or damage transfers completely to the User upon transfer of the items to the shipping
              carrier (FOB Shipping Point).
            </p>
          </div>
          <div className={styles.subsection}>
            <h3>3.5 International Shipping (Future)</h3>
            <p>
              International shipping may be introduced in the future, subject to applicable laws and fulfillment
              capabilities. Any such availability will be clearly communicated on the website at the time of purchase.
            </p>
          </div>
        </section>
  
        <div className={styles.policyLinks}>
          <Link to="/terms" className={styles.policyLink}>
            Terms & Conditions
          </Link>
          <Link to="/privacy" className={styles.policyLink}>
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}

export default RefundPolicy;